import { useState, useMemo } from 'react'
import { ZodiacSignNames, CyberPlanetNames } from '../types'
import type { ChartData, ZodiacSign, Planet } from '../types'

interface Chart2DProps {
  chartData: ChartData
  size?: number
}

// 星座颜色 - 柔和版本（更适合女性用户）
const zodiacColors: Record<ZodiacSign, string> = {
  Aries: '#ffb3ba',    // 火 - 柔和粉红
  Leo: '#ffdfba',      // 火 - 柔和橙
  Sagittarius: '#ffffba', // 火 - 柔和黄
  Taurus: '#baffc9',   // 土 - 柔和绿
  Virgo: '#a8e6cf',    // 土 - 薄荷绿
  Capricorn: '#98ddca', // 土 - 青绿
  Gemini: '#bae1ff',   // 风 - 柔和蓝
  Libra: '#e0f7fa',    // 风 - 天蓝
  Aquarius: '#d1c4e9', // 风 - 淡紫
  Cancer: '#c5cae9',   // 水 - 淡蓝
  Scorpio: '#b3e5fc',  // 水 - 浅蓝
  Pisces: '#f8bbd0',   // 水 - 淡粉
}

// 行星颜色
const planetColors: Record<string, string> = {
  sun: '#FFD700',
  moon: '#C0C0C0',
  mercury: '#87CEEB',
  venus: '#FFB6C1',
  mars: '#FF6B6B',
  jupiter: '#D4A373',
  saturn: '#708090',
  uranus: '#40E0D0',
  neptune: '#4169E1',
  pluto: '#8B008B',
}

// 星座符号
const zodiacSymbols: Record<string, string> = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋',
  Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏',
  Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}

// 行星符号
const planetSymbols: Record<string, string> = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇',
}

// 宫位背景色
const houseBgColors = [
  '#FFF5F5', '#FFF9E6', '#F0FFF4', '#E6FFFA', '#EBF8FF', '#F3E8FF',
  '#FFF5F5', '#FFF9E6', '#F0FFF4', '#E6FFFA', '#EBF8FF', '#F3E8FF',
]

export default function Chart2D({ chartData, size = 500 }: Chart2DProps) {
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null)
  const [showAspects, setShowAspects] = useState(true)

  const center = size / 2
  const outerRadius = size * 0.48
  const zodiacRadius = size * 0.42
  const planetRadius = size * 0.32
  const innerRadius = size * 0.18

  // 计算位置
  const getPosition = (longitude: number, radius: number) => {
    // 减去90度使上升点在左边（西边）
    const angle = ((longitude - 90) * Math.PI) / 180
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    }
  }

  // 宫位扇形
  const houseSectors = useMemo(() => {
    return chartData.houses.map((house, index) => {
      const startAngle = ((house.degree - 90) * Math.PI) / 180
      const nextHouse = chartData.houses[(index + 1) % 12]
      const endAngle = ((nextHouse.degree - 90) * Math.PI) / 180

      const largeArc = endAngle - startAngle > Math.PI ? 1 : 0

      const x1 = center + zodiacRadius * Math.cos(startAngle)
      const y1 = center + zodiacRadius * Math.sin(startAngle)
      const x2 = center + zodiacRadius * Math.cos(endAngle)
      const y2 = center + zodiacRadius * Math.sin(endAngle)

      const path = `
        M ${center} ${center}
        L ${x1} ${y1}
        A ${zodiacRadius} ${zodiacRadius} 0 ${largeArc} 1 ${x2} ${y2}
        Z
      `

      return {
        path,
        house,
        index,
        color: houseBgColors[index],
      }
    })
  }, [chartData.houses, center, zodiacRadius])

  // 黄道十二宫外圈
  const zodiacRing = useMemo(() => {
    const sectors = []
    for (let i = 0; i < 12; i++) {
      const startAngle = (i * 30 - 90) * Math.PI / 180
      const endAngle = ((i + 1) * 30 - 90) * Math.PI / 180
      const largeArc = 0

      const innerR = zodiacRadius
      const outerR = outerRadius

      const x1 = center + innerR * Math.cos(startAngle)
      const y1 = center + innerR * Math.sin(startAngle)
      const x2 = center + outerR * Math.cos(startAngle)
      const y2 = center + outerR * Math.sin(startAngle)
      const x3 = center + outerR * Math.cos(endAngle)
      const y3 = center + outerR * Math.sin(endAngle)
      const x4 = center + innerR * Math.cos(endAngle)
      const y4 = center + innerR * Math.sin(endAngle)

      const path = `
        M ${x1} ${y1}
        L ${x2} ${y2}
        A ${outerR} ${outerR} 0 ${largeArc} 1 ${x3} ${y3}
        L ${x4} ${y4}
        A ${innerR} ${innerR} 0 ${largeArc} 0 ${x1} ${y1}
      `

      const zodiacSign = Object.keys(zodiacSymbols)[i] as ZodiacSign
      const midAngle = ((i + 0.5) * 30 - 90) * Math.PI / 180
      const labelR = (innerR + outerR) / 2
      const labelX = center + labelR * Math.cos(midAngle)
      const labelY = center + labelR * Math.sin(midAngle)

      sectors.push({
        path,
        zodiacSign,
        symbol: zodiacSymbols[zodiacSign],
        color: zodiacColors[zodiacSign],
        labelX,
        labelY,
      })
    }
    return sectors
  }, [center, outerRadius, zodiacRadius])

  // 行星位置
  const planetPositions = useMemo(() => {
    return Object.entries(chartData.planets).map(([name, planet]) => {
      const pos = getPosition(planet.longitude, planetRadius)
      return {
        name,
        planet,
        x: pos.x,
        y: pos.y,
        color: planetColors[name],
        symbol: planetSymbols[name],
        label: CyberPlanetNames[name as Planet] || name,
      }
    })
  }, [chartData.planets, planetRadius])

  // 相位线
  const aspectLines = useMemo(() => {
    if (!showAspects) return []
    
    return chartData.aspects.slice(0, 15).map((aspect, index) => {
      const p1 = chartData.planets[aspect.planet1]
      const p2 = chartData.planets[aspect.planet2]
      if (!p1 || !p2) return null

      const pos1 = getPosition(p1.longitude, planetRadius * 0.8)
      const pos2 = getPosition(p2.longitude, planetRadius * 0.8)

      let color = '#ccc'
      let width = 1
      let dash = ''

      switch (aspect.aspect) {
        case 'conjunction':
          color = '#00f0ff'
          width = 3
          break
        case 'trine':
          color = '#00ff9d'
          width = 2
          break
        case 'square':
          color = '#ff00a0'
          width = 2
          break
        case 'opposition':
          color = '#ff6b6b'
          width = 2
          break
        case 'sextile':
          color = '#9d00ff'
          width = 1
          dash = '4,4'
          break
      }

      return {
        x1: pos1.x,
        y1: pos1.y,
        x2: pos2.x,
        y2: pos2.y,
        color,
        width,
        dash,
        key: index,
      }
    }).filter(Boolean)
  }, [chartData, planetRadius, showAspects])

  // 上升点位置
  const ascendantPos = getPosition(chartData.ascendant.longitude, outerRadius + 15)

  return (
    <div className="relative">
      {/* 控制栏 */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <button
          onClick={() => setShowAspects(!showAspects)}
          className={`px-3 py-1 rounded-full text-xs border transition-colors ${
            showAspects 
              ? 'bg-cyber-blue/20 text-cyber-blue border-cyber-blue/30' 
              : 'bg-cyber-dark/50 text-gray-400 border-gray-700'
          }`}
        >
          {showAspects ? '✓' : ''} 显示相位
        </button>
      </div>

      <div className="cyber-card p-4 inline-block">
        <svg 
          width={size} 
          height={size} 
          viewBox={`0 0 ${size} ${size}`}
          className="select-none"
        >
          {/* 背景圆 */}
          <circle 
            cx={center} 
            cy={center} 
            r={outerRadius} 
            fill="#0a0a0f" 
            stroke="#00f0ff" 
            strokeWidth="2"
            strokeOpacity="0.3"
          />

          {/* 宫位扇形 */}
          {houseSectors.map(({ path, color, index }) => (
            <path
              key={`house-${index}`}
              d={path}
              fill={color}
              fillOpacity="0.1"
              stroke="#00f0ff"
              strokeWidth="0.5"
              strokeOpacity="0.2"
            />
          ))}

          {/* 宫位数字 */}
          {chartData.houses.map((house, index) => {
            const angle = ((house.degree + (chartData.houses[(index + 1) % 12]?.degree || house.degree + 30)) / 2 - 90) * Math.PI / 180
            const r = zodiacRadius - 15
            const x = center + r * Math.cos(angle)
            const y = center + r * Math.sin(angle)
            return (
              <text
                key={`house-num-${index}`}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs"
                fill="#666"
                fontSize="10"
              >
                {house.number}
              </text>
            )
          })}

          {/* 黄道十二宫外圈 */}
          {zodiacRing.map(({ path, symbol, color, labelX, labelY }, index) => (
            <g key={`zodiac-${index}`}>
              <path
                d={path}
                fill={color}
                fillOpacity="0.15"
                stroke={color}
                strokeWidth="1"
                strokeOpacity="0.5"
              />
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color}
                fontSize="16"
              >
                {symbol}
              </text>
            </g>
          ))}

          {/* 内圈 */}
          <circle 
            cx={center} 
            cy={center} 
            r={innerRadius} 
            fill="#0f0f1a" 
            stroke="#00f0ff" 
            strokeWidth="1"
            strokeOpacity="0.3"
          />

          {/* 中心信息 */}
          <text
            x={center}
            y={center - 8}
            textAnchor="middle"
            fill="#fff"
            fontSize="12"
            fontWeight="500"
          >
            {zodiacSymbols[chartData.ascendant.sign]}
          </text>
          <text
            x={center}
            y={center + 12}
            textAnchor="middle"
            fill="#00f0ff"
            fontSize="10"
          >
            ASC
          </text>

          {/* 相位线 */}
          {aspectLines.map((line: any) => (
            <line
              key={line.key}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke={line.color}
              strokeWidth={line.width}
              strokeDasharray={line.dash}
              opacity={0.6}
            />
          ))}

          {/* 行星 */}
          {planetPositions.map(({ name, x, y, color, symbol, planet, label }) => (
            <g
              key={name}
              onMouseEnter={() => setHoveredPlanet(name)}
              onMouseLeave={() => setHoveredPlanet(null)}
              className="cursor-pointer"
            >
              {/* 行星光环 */}
              <circle
                cx={x}
                cy={y}
                r={hoveredPlanet === name ? 18 : 12}
                fill={color}
                fillOpacity={hoveredPlanet === name ? 0.3 : 0.1}
                stroke={color}
                strokeWidth={hoveredPlanet === name ? 2 : 1}
              />
              
              {/* 行星符号 */}
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color}
                fontSize={hoveredPlanet === name ? 18 : 14}
                fontWeight="bold"
                style={{ 
                  filter: `drop-shadow(0 0 ${hoveredPlanet === name ? '4px' : '2px'} ${color})`,
                  transition: 'all 0.2s'
                }}
              >
                {symbol}
              </text>

              {/* 度数标记 */}
              <text
                x={x}
                y={y + 20}
                textAnchor="middle"
                fill="#888"
                fontSize="9"
              >
                {planet.degree.toFixed(0)}°
              </text>

              {/* 逆行标记 */}
              {planet.retrograde && (
                <text
                  x={x + 10}
                  y={y - 8}
                  fill="#ff6b6b"
                  fontSize="8"
                >
                  ℞
                </text>
              )}

              {/* 悬停提示 */}
              {hoveredPlanet === name && (
                <g>
                  <rect
                    x={x + 20}
                    y={y - 40}
                    width={100}
                    height={60}
                    rx={8}
                    fill="#0f0f1a"
                    stroke="#00f0ff"
                    strokeWidth="1"
                    strokeOpacity="0.3"
                  />
                  <text
                    x={x + 70}
                    y={y - 22}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize="11"
                    fontWeight="500"
                  >
                    {label}
                  </text>
                  <text
                    x={x + 70}
                    y={y - 8}
                    textAnchor="middle"
                    fill="#00f0ff"
                    fontSize="10"
                  >
                    {ZodiacSignNames[planet.sign]} {planet.degree.toFixed(1)}°
                  </text>
                  <text
                    x={x + 70}
                    y={y + 6}
                    textAnchor="middle"
                    fill="#888"
                    fontSize="9"
                  >
                    第{planet.house}宫
                  </text>
                  {planet.retrograde && (
                    <text
                      x={x + 70}
                      y={y + 20}
                      textAnchor="middle"
                      fill="#ff6b6b"
                      fontSize="9"
                    >
                      逆行
                    </text>
                  )}
                </g>
              )}
            </g>
          ))}

          {/* 上升点标记 */}
          <g>
            <polygon
              points={`${ascendantPos.x},${ascendantPos.y} ${ascendantPos.x - 8},${ascendantPos.y - 6} ${ascendantPos.x - 8},${ascendantPos.y + 6}`}
              fill="#ffd700"
            />
            <text
              x={ascendantPos.x + 15}
              y={ascendantPos.y}
              fill="#ffd700"
              fontSize="10"
              fontWeight="500"
            >
              ASC {chartData.ascendant.degree.toFixed(0)}°
            </text>
          </g>

          {/* 天顶标记 */}
          {chartData.midheaven && (
            <g>
              <text
                x={center}
                y={center - outerRadius - 10}
                textAnchor="middle"
                fill="#9d00ff"
                fontSize="10"
                fontWeight="500"
              >
                MC {chartData.midheaven.degree.toFixed(0)}°
              </text>
            </g>
          )}
        </svg>

        {/* 图例 */}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {Object.entries(planetColors).slice(0, 7).map(([name, color]) => (
            <div key={name} className="flex items-center gap-1">
              <span 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-gray-400">
                {CyberPlanetNames[name as Planet]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 底部信息 */}
      <div className="mt-4 text-center text-xs text-gray-500">
        <p>上升 {ZodiacSignNames[chartData.ascendant.sign]} • 天顶 {chartData.midheaven ? ZodiacSignNames[chartData.midheaven.sign] : '-'} • 悬停查看详情</p>
      </div>
    </div>
  )
}
